import React, { useState, useEffect } from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown, GripVertical } from 'lucide-react';

/**
 * Admin designer for an assessment's `company_info_schema`.
 * Two halves:
 *   1) Built-in field visibility/required toggles
 *   2) Custom fields list (add / remove / reorder / type-specific options)
 *
 * Props:
 *   value:    current schema = { builtin: {...}, custom_fields: [...] } | null
 *   onChange: (newSchema) => void
 */
const BUILTIN_FIELDS = [
  { key: 'company_name',  label: 'Company Name'  },
  { key: 'contact_email', label: 'Contact Email' },
  { key: 'contact_phone', label: 'Phone'          },
  { key: 'nace_codes',    label: 'NACE Activity Codes' },
];

const FIELD_TYPES = [
  { value: 'text',         label: 'Text (single line)' },
  { value: 'textarea',     label: 'Text (paragraph)' },
  { value: 'number',       label: 'Number' },
  { value: 'email',        label: 'Email' },
  { value: 'phone',        label: 'Phone' },
  { value: 'date',         label: 'Date' },
  { value: 'dropdown',     label: 'Dropdown' },
  { value: 'radio',        label: 'Radio buttons' },
  { value: 'multi-select', label: 'Multi-select' },
  { value: 'checkbox',     label: 'Checkbox' },
  { value: 'consent',      label: 'Consent (one-off, not stored on profile)' },
  { value: 'file',         label: 'File upload' },
];

export default function CompanyInfoSchemaDesigner({ value, onChange }) {
  const [schema, setSchema] = useState(value || { builtin: {}, custom_fields: [] });
  useEffect(() => { setSchema(value || { builtin: {}, custom_fields: [] }); }, [value]);

  const update = (next) => { setSchema(next); onChange(next); };

  const setBuiltin = (key, mode) => update({
    ...schema,
    builtin: { ...(schema.builtin || {}), [key]: mode },
  });

  const setCustom = (idx, patch) => {
    const arr = [...(schema.custom_fields || [])];
    arr[idx] = { ...arr[idx], ...patch };
    update({ ...schema, custom_fields: arr });
  };
  const addCustom = () => update({
    ...schema,
    custom_fields: [...(schema.custom_fields || []), {
      key: `field_${(schema.custom_fields || []).length + 1}`,
      label: 'New field',
      type: 'text',
      required: false,
      options: [],
    }],
  });
  const removeCustom = (idx) => {
    const arr = [...(schema.custom_fields || [])];
    arr.splice(idx, 1);
    update({ ...schema, custom_fields: arr });
  };
  const moveCustom = (idx, dir) => {
    const arr = [...(schema.custom_fields || [])];
    const j = dir === 'up' ? idx - 1 : idx + 1;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    update({ ...schema, custom_fields: arr });
  };

  const inputCls = 'w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 outline-none focus:ring-1 focus:ring-emerald-500';

  return (
    <div data-testid="company-info-schema-designer" className="space-y-6">
      {/* Built-in toggles */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Built-in Fields</h4>
        <p className="text-xs text-gray-500 mb-3">Choose how each built-in field appears in the wizard.</p>
        <div className="space-y-2">
          {BUILTIN_FIELDS.map(({ key, label }) => {
            const mode = (schema.builtin && schema.builtin[key]) || 'required';
            return (
              <div key={key} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <span className="text-sm text-gray-800">{label}</span>
                <select value={mode} onChange={(e) => setBuiltin(key, e.target.value)}
                  data-testid={`builtin-${key}-mode`}
                  className="bg-white border border-gray-300 rounded px-2 py-1 text-xs text-gray-900 outline-none">
                  <option value="required">Required</option>
                  <option value="optional">Optional</option>
                  <option value="hidden">Hidden</option>
                </select>
              </div>
            );
          })}
        </div>
      </div>

      {/* Custom fields */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-gray-700">Custom Fields</h4>
          <button onClick={addCustom} type="button" data-testid="add-custom-field-btn"
            className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1">
            <Plus className="w-3 h-3" /> Add field
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-3">Custom fields are saved on the execution. Non-consent fields are also mirrored to the user's profile so future assessments pre-fill them.</p>

        {(schema.custom_fields || []).length === 0 && (
          <p className="text-xs text-gray-400 italic">No custom fields yet.</p>
        )}

        <div className="space-y-3">
          {(schema.custom_fields || []).map((f, idx) => {
            const optional = ['dropdown', 'radio', 'multi-select', 'multiselect'].includes((f.type || '').toLowerCase());
            return (
              <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3 space-y-2"
                data-testid={`custom-field-row-${idx}`}>
                <div className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4 text-gray-300" />
                  <input value={f.label || ''} onChange={(e) => setCustom(idx, { label: e.target.value })}
                    placeholder="Label"
                    className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm" />
                  <input value={f.key || ''} onChange={(e) => setCustom(idx, { key: e.target.value.replace(/[^a-zA-Z0-9_]/g, '_') })}
                    placeholder="key (snake_case)"
                    className="w-44 border border-gray-300 rounded px-2 py-1.5 text-xs font-mono" />
                  <select value={f.type || 'text'} onChange={(e) => setCustom(idx, { type: e.target.value })}
                    className="border border-gray-300 rounded px-2 py-1.5 text-xs">
                    {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <div className="flex flex-col gap-0.5">
                    <button type="button" onClick={() => moveCustom(idx, 'up')} disabled={idx === 0}
                      className="text-gray-400 hover:text-emerald-600 disabled:opacity-30">
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => moveCustom(idx, 'down')} disabled={idx === (schema.custom_fields || []).length - 1}
                      className="text-gray-400 hover:text-emerald-600 disabled:opacity-30">
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <button type="button" onClick={() => removeCustom(idx)} className="text-red-400 hover:text-red-600 p-1">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-2 pl-6">
                  <input value={f.placeholder || ''} onChange={(e) => setCustom(idx, { placeholder: e.target.value })}
                    placeholder="Placeholder" className="border border-gray-200 rounded px-2 py-1 text-xs" />
                  <input value={f.help || ''} onChange={(e) => setCustom(idx, { help: e.target.value })}
                    placeholder="Help text" className="border border-gray-200 rounded px-2 py-1 text-xs" />
                  <label className="flex items-center gap-2 text-xs text-gray-700">
                    <input type="checkbox" checked={!!f.required} onChange={(e) => setCustom(idx, { required: e.target.checked })}
                      className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" />
                    Required
                  </label>
                </div>

                {optional && (
                  <div className="pl-6">
                    <label className="block text-xs text-gray-500 mb-1">Options (one per line — value or value|label)</label>
                    <textarea
                      value={(f.options || []).map(o => typeof o === 'string' ? o : `${o.value}|${o.label}`).join('\n')}
                      onChange={(e) => {
                        const lines = e.target.value.split('\n').map(s => s.trim()).filter(Boolean);
                        const opts = lines.map((line) => {
                          const [val, lbl] = line.split('|').map(s => s.trim());
                          return lbl ? { value: val, label: lbl } : val;
                        });
                        setCustom(idx, { options: opts });
                      }}
                      rows={3}
                      className={`${inputCls} resize-none`} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
